import { FrequencyUnit } from '@griply/types';
import {
  addMonths,
  addWeeks,
  endOfWeek,
  format,
  isSameMonth,
  isSameWeek,
  isSameYear,
  startOfWeek,
} from 'date-fns';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { weekdaysNumberMap } from 'shared/constants';
import { useToday } from 'shared/contexts/today';
import { getStartTillEndLabel } from 'shared/utils/get-start-till-end-label';

import { useData } from '../../use-data';
import { UpcomingPeriod, UpcomingPeriodTypes } from '../upcoming/types';

export type UsePeriodLabelOptions = {
  period: UpcomingPeriod;
};

export const usePeriodLabel = ({ period }: UsePeriodLabelOptions) => {
  const { t } = useTranslation();
  const today = useToday();
  const { weekStartsOn, dateFormat } = useData();

  return useMemo(() => {
    if (period.type === UpcomingPeriodTypes.Overdue) {
      return t('general.labels.overdue');
    }

    const dateFnsOptions = { weekStartsOn: weekdaysNumberMap[weekStartsOn] };
    const periodDate = period.date;

    if (period.type === FrequencyUnit.Month) {
      if (isSameMonth(periodDate, today)) {
        return t('dialogs.task-detail.meta.upcoming.this-month.label');
      }

      if (isSameMonth(periodDate, addMonths(today, 1))) {
        return t('dialogs.task-detail.meta.upcoming.next-month.label');
      }

      if (isSameYear(periodDate, today)) {
        return format(periodDate, 'MMMM');
      }

      return format(periodDate, 'MMMM yyyy');
    }

    if (period.type === FrequencyUnit.Year) {
      return format(periodDate, 'yyyy');
    }

    if (isSameWeek(periodDate, today, dateFnsOptions)) {
      return t('dialogs.task-detail.meta.upcoming.this-week.label');
    }

    if (isSameWeek(periodDate, addWeeks(today, 1), dateFnsOptions)) {
      return t('dialogs.task-detail.meta.upcoming.next-week.label');
    }

    return getStartTillEndLabel({
      startDate: startOfWeek(periodDate, dateFnsOptions),
      endDate: endOfWeek(periodDate, dateFnsOptions),
      today,
      t,
      dateFormat,
    });
  }, [t, today, period.date, period.type, weekStartsOn, dateFormat]);
};
